import { api } from './api';

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query({
      query: (params) => ({
        url: '/users',
        params,
      }),
      providesTags: ['User'],
    }),
    /** Admin users list: GET /admin/users with startDate, endDate, search, page, limit */
    getAdminUsers: builder.query({
      query: (params = {}) => ({
        url: '/admin/users',
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.search && { search: params.search }),
        },
      }),
      providesTags: ['User'],
    }),
    /** Admin: GET /admin/users/:id */
    getAdminUser: builder.query({
      query: (id) => ({ url: `/admin/users/${id}` }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    /** Admin: DELETE /admin/users/:id */
    deleteAdminUser: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    /** Admin: POST /admin/users/:id/login-as */
    loginAsAdminUser: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}/login-as`,
        method: 'POST',
      }),
    }),
    /** Admin: PATCH /admin/users/:id/credits */
    updateAdminUserCredits: builder.mutation({
      query: ({ id, credits }) => ({
        url: `/admin/users/${id}/credits`,
        method: 'PATCH',
        body: { credits },
      }),
      invalidatesTags: ['User'],
    }),
    /** Admin: PATCH /admin/users/:id/private-creations */
    updateAdminUserPrivateCreations: builder.mutation({
      query: ({ id, private_creations }) => ({
        url: `/admin/users/${id}/private-creations`,
        method: 'PATCH',
        body: { private_creations },
      }),
      invalidatesTags: ['User'],
    }),
    /** Admin: PATCH /admin/users/:id/type */
    updateAdminUserType: builder.mutation({
      query: ({ id, type }) => ({
        url: `/admin/users/${id}/type`,
        method: 'PATCH',
        body: { type },
      }),
      invalidatesTags: ['User'],
    }),
    /** Admin: GET /admin/paid-members - users with active subscription */
    getPaidMembers: builder.query({
      query: (params = {}) => ({
        url: '/admin/paid-members',
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.search && { search: params.search }),
        },
      }),
      providesTags: ['User'],
    }),
    /** Admin: GET /admin/paid-members/:id - paid member detail with subscriptions */
    getPaidMember: builder.query({
      query: (id) => ({ url: `/admin/paid-members/${id}` }),
      providesTags: (result, error, id) => [{ type: 'User', id: `paid-member-${id}` }],
    }),
    getUser: builder.query({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    createUser: builder.mutation({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    getUserFollowStats: builder.query({
      query: (username) => `/profile/users/${username}/follow-stats`,
      providesTags: (result, error, username) => [{ type: 'User', id: `follow-stats-${username}` }],
    }),
    getUserFollowers: builder.query({
      query: ({ username, page = 1, limit = 10 }) => ({
        url: `/profile/users/${username}/followers`,
        params: { page, limit },
      }),
      providesTags: (result, error, { username }) => [{ type: 'User', id: `followers-${username}` }],
    }),
    getUserFollowing: builder.query({
      query: ({ username, page = 1, limit = 10 }) => ({
        url: `/profile/users/${username}/following`,
        params: { page, limit },
      }),
      providesTags: (result, error, { username }) => [{ type: 'User', id: `following-${username}` }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useDeleteAdminUserMutation,
  useLoginAsAdminUserMutation,
  useUpdateAdminUserCreditsMutation,
  useUpdateAdminUserPrivateCreationsMutation,
  useUpdateAdminUserTypeMutation,
  useGetPaidMembersQuery,
  useGetPaidMemberQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserFollowStatsQuery,
  useGetUserFollowersQuery,
  useGetUserFollowingQuery,
} = usersApi;

